import React, { memo, useEffect, useMemo, useState, useCallback } from 'react';

import { Link } from '@Sage/hoi-poi-ui';

import CopilotChart from '../../Chart';
import CopilotTable from '../../Table';

const validLinkAttributes = ['id'];
const DANGEROUS_HREF = /^(javascript|data|vbscript|file|blob):/i;

const compactHref = (href) =>
    Array.from(String(href ?? '').trim())
        .filter((char) => {
            const code = char.charCodeAt(0);
            return code > 32 && code !== 127;
        })
        .join('');

const isDangerousHref = (href) => {
    if (!href) return false;
    const candidates = [String(href).trim(), compactHref(href)];
    try {
        candidates.push(decodeURIComponent(String(href)), decodeURIComponent(compactHref(href)));
    } catch {
        // Malformed percent-encoding is not a bypass.
    }
    return candidates.some((value) => DANGEROUS_HREF.test(value));
};

const CustomLink = ({ title, loading, tables, reports, node: _node, ...props }) => {
    const [parsedData, setParsedData] = useState({ title: undefined, attributes: {} });

    const { href, ...otherProps } = props;

    const parseTitleString = useCallback((str) => {
        try {
            const parsedJson = JSON.parse(str);
            const finalAttributes = Object.entries(parsedJson).reduce((acc, [key, value]) => {
                if (validLinkAttributes.includes(key)) {
                    acc[key] = value;
                }
                return acc;
            }, {});
            setParsedData({ title: undefined, attributes: finalAttributes });
        } catch (error) {
            setParsedData({ title: str, attributes: {} });
        }
    }, []);

    const { Component, finalProps } = useMemo(() => {
        const { attributes, title: finalTitle } = parsedData;

        if (href?.includes('fm_table')) {
            return {
                Component: CopilotTable,
                finalProps: {
                    id: attributes.id,
                    loading,
                    data: (tables || {})[attributes.id] || [],
                },
            };
        }

        if (href?.includes('fm_report')) {
            return {
                Component: CopilotChart,
                finalProps: {
                    id: attributes.id,
                    loading,
                    ...((reports || {})[attributes.id] || {}),
                },
            };
        }

        if (isDangerousHref(href)) {
            return {
                Component: 'span',
                finalProps: otherProps,
            };
        }

        const { id, ...otherAttrs } = attributes;

        return {
            Component: Link,
            finalProps: {
                target: '_blank',
                title: finalTitle,
                variation: 'primary',
                ...otherAttrs,
                ...otherProps,
                href,
            },
        };
    }, [parsedData, href, otherProps, loading, tables, reports]);

    useEffect(() => {
        if (title) {
            parseTitleString(title);
        }
    }, [title, parseTitleString]);

    return <Component {...finalProps} />;
};

export default memo(CustomLink);
